import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import useFetch from '../../hooks/useFetch';

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUniversity,
  faUserGraduate,
  faShieldAlt,
  faBookOpen,
  faBrain,
  faExchangeAlt,
  faChartLine,
  faCog,
  faCheckCircle,
  faClock,
  faTimesCircle,
  faCode,
  faLock,
  faChartBar,
  faPuzzlePiece,
  faDownload,
  faEye,
  faPrint,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  RadialLinearScale,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";

import { Radar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  RadialLinearScale,
  Filler,
  Tooltip,
  Legend
);

import AnugyaTestDummyData from './AnugyaTestDummyData';
import AnugyaROICalc from "./AnugyaROICalc";
import AnugyaPriceList from "./AnugyaPriceList";
import AnugyaStartJourney from "./AnugyaStartJourney";
import AnugyaDemoSection from "./AnugyaDemoSection";
import AnugyaAI from "./AnugyaAI";

import { logout } from "../../redux/actions/authActions";

export default function AnugyaHome() {
  const authState = useSelector(state => state.authReducer);
  const [fetchData, { loading }] = useFetch();
  const navigate = useNavigate();
  const location = useLocation();

  const isLoggedIn = authState?.isLoggedIn || false;

  const dispatch = useDispatch();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
  dispatch(logout());
  navigate("/");
  };
 
  const [uId, setUId] = useState(authState.user._id);
  const [userName, setUserName] = useState(authState.user.name);
  
  const [uCode, setUCode] = useState(authState.user.think5whyscode);
  const {
    consentRequests,
    myRequests,
    completedWork,
    eventStream,
    DOCUMENTS,
    CONSENTS,
    ACCESS_LOG,
    CONSENT_LIST,
    ACTION_LIST,
    USER_DB
  } = AnugyaTestDummyData;
  
  const [docsState, setDocsState] = useState(DOCUMENTS);
  const [showNewDocForm, setShowNewDocForm] = useState(false);

  const [newDoc, setNewDoc] = useState({
    category: "",
    name: "",
    type: "",
    version: "",
    description: "",
    fileName: "",
    filePath: "",
    fileKey: "",
    mimeType: "application/pdf",
    fileSize: "",
    storageType: "local",
    consentEnabled: false,
    accessEntities: "",
    actions: "",
    createdAt: "",
    userId: uId,
    userCode: uCode,
  });

  const selectedDocRef = useRef(null);
  const [showDocs, setShowDocs] = useState(false);
  const docCount = DOCUMENTS.reduce((acc, category) => acc + category.items.length,0);
  const [selectedDoc, setSelectedDoc] = useState(null);

  const [showConsents, setShowConsents] = useState(false);
  const activeConsentCount = CONSENTS.filter((c) => c.status === "active").length;
  const [activeView, setActiveView] = useState(null);
  const closeActiveView = () => setActiveView(null);

  const [showSignals, setShowSignals] = useState(false);

  const [showCopyPanel, setShowCopyPanel] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);
  const [qrDoc, setQrDoc] = useState(null);
  
  const [activePanel, setActivePanel] = useState(null);
  const [actionMode, setActionMode] = useState(null);

  const [userCode, setUserCode] = useState("");
  const [verifiedUser, setVerifiedUser] = useState(null);
  const [verifyError, setVerifyError] = useState("");

  const [consentNote, setConsentNote] = useState("");
  const hasConsent = CONSENT_LIST.some((c) => c.docId === selectedDoc?.id);

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadError, setUploadError] = useState("");

  useEffect(() => {
    if (!selectedDoc || !selectedDocRef.current) return;

    requestAnimationFrame(() => {
      const top = selectedDocRef.current.getBoundingClientRect().top;
      const offset = 90; 

      window.scrollTo({
        top: window.scrollY + top - offset,
        behavior: "smooth",
      });
    });
  }, [selectedDoc]);

  const handleClosePanel = () => {
    setActivePanel(null);
    setActionMode(null);
    setUserCode("");
    setVerifiedUser(null);
    setVerifyError(null);
    setConsentNote("");
  };

  const openPanel = (panel) => {
    setActivePanel(panel);
    setActionMode(null); 
  };

  const openActionMode = (mode) => {
    setActionMode(mode);
    setActivePanel(null); 
  };

  const [viewMode, setViewMode] = useState(null);

  const generateDocLink = (docId) => {
    return `https://www.think5whys.com/anugya/${docId}`;
  };

  const handleOpen = (doc) => {
    const fileUrl = `http://localhost:3001/api/documents/view/${doc.fileName}`;
    window.open(fileUrl, "_blank");
  };

  const handleCopyLink = (doc) => {
    const link = generateDocLink(doc.id);

    navigator.clipboard.writeText(link);
    setCopiedLink(link);

    setShowCopyPanel(true);
    
    setQrDoc(
      `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
        link
      )}`
    );
  };

  const handleShare = async (doc) => {
    const link = generateDocLink(doc.id);

    const shareData = {
      title: doc.name,
      text: `Shared via Think5Whys Anugya OS: ${doc.name}`,
      url: link,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        alert("Sharing not supported on this device");
      }
    } catch (err) {
      console.log(err);
    }
  };

  const [consentRecords, setConsentRecords] = useState([
    {
      id: 1,
      date: "2026-05-15",
      code: "T5W1001",
      name: "Rahul Mehta",
      days: 3,
    },
    {
      id: 2,
      date: "2026-05-10",
      code: "T5W1002",
      name: "Priya Sharma",
      days: 8,
    },
  ]);

  const handleVerify = () => {
    const user = USER_DB[userCode];

    if (user) {
      setVerifiedUser(user);
      setVerifyError("");
    } else {
      setVerifiedUser(null);
      setVerifyError("Code not Found");
    }
  };

  const handlePostConsent = () => {
    if (!verifiedUser) return;

    const newConsent = {
      id: Date.now(),
      date: new Date().toISOString().split("T")[0],
      code: userCode,
      name: verifiedUser.name,
      days: 0,
    };

    setConsentRecords([newConsent, ...consentRecords]);

    setUserCode("");
    setVerifiedUser(null);
    setConsentNote("");
    setActionMode(null);
  };

  const handleRevoke = (id) => {setConsentRecords(consentRecords.filter((c) => c.id !== id));};
  
  const ActionButton = ({ label, onClick, highlight, danger }) => {
    return (
      <button
        onClick={onClick}

        className={`
          text-xs md:text-sm px-3 py-2 rounded-xl border transition
          hover:shadow-md flex items-center justify-center
          ${
            highlight
              ? "bg-green-600 text-white border-green-600"
              : danger
              ? "bg-red-700 text-white border-red-200"
              : "bg-blue-600 text-slate-100 border-slate-200"
          }
        `}
      >
        {label}
      </button>
    );
  };

  const fetchMyDocuments = async () => {
    try {
      const config = {
        url: "/api/anugya/documents/my-docs",
        method: "post",
        data: { userId },
        headers: {
          Authorization: authState.token,
        },
      };

      const res = await fetchData(config);

      if (res?.status) {
        setDocsState(res.documents);
      }
    } catch (err) {
      console.error("Fetch docs error:", err);
    }
  };

  // const handleSaveNewDoc = async () => {
  //   if (!selectedFile) {
  //     setUploadError("Please select a file");
  //     return;
  //   }

  //   if (!newDoc.category || !newDoc.name) return;

  //   try {
  //     const formData = new FormData();

  //     formData.append("file", selectedFile);
  //     formData.append("userId", newDoc.userId || "T5W1001");
  //     formData.append("userCode", newDoc.userCode || "T5W1001");
  //     formData.append("userName", "Demo User");
  //     formData.append("title", newDoc.name);
  //     formData.append("description", newDoc.type || "");
  //     formData.append("isActive", true);
  //     formData.append("dateCreated", new Date().toISOString());

  //     const res = await fetch("http://localhost:5000/upload-files", {
  //       method: "POST",
  //       body: formData,
  //     });

  //     const data = await res.json();

  //     if (!res.ok) {
  //       throw new Error(data.message || "Upload failed");
  //     }
  //     const docToInsert = {
  //       ...newDoc,
  //       id: `DOC-${Date.now()}`,
  //       version: newDoc.version || "v1.0",
  //       description: newDoc.description || "",
  //       filePath: data.filePath,
  //       fileName: selectedFile.name,
  //       fileSize: `${(selectedFile.size / 1024).toFixed(1)} KB`,
  //       accessEntities: newDoc.accessEntities
  //         ? newDoc.accessEntities.split(",").map((x) => x.trim())
  //         : [],
  //       actions: newDoc.actions
  //         ? newDoc.actions.split(",").map((x) => x.trim())
  //         : [],
  //     };

  //     setDocsState((prev) =>
  //       prev.map((cat) => {
  //         if (cat.category === newDoc.category) {
  //           return {
  //             ...cat,
  //             items: [docToInsert, ...cat.items],
  //           };
  //         }
  //         return cat;
  //       })
  //     );

  //     // RESET UI
  //     setShowNewDocForm(false);
  //     setSelectedFile(null);

  //     setNewDoc({
  //       category: "",
  //       name: "",
  //       type: "",
  //       version: "",
  //       description: "",
  //       fileName: "",
  //       filePath: "",
  //       fileKey: "",
  //       mimeType: "application/pdf",
  //       fileSize: "",
  //       storageType: "local",
  //       consentEnabled: false,
  //       accessEntities: "",
  //       actions: "",
  //       createdAt: "",
  //       userId: uId,
  //       userCode: uCode,
  //     });

  //   } catch (err) {
  //     setUploadError(err.message);
  //   }
  // };

  const handleSaveNewDoc = async (e) => {
    e.preventDefault();

    if (isSaving) return;

    const errors = {};

    if (!newDoc.name?.trim()) {
      errors.name = "Document name is required";
    }

    if (!newDoc.category?.trim()) {
      errors.category = "Category is required";
    }

    if (!selectedFile) {
      setUploadError("Please select a file");
      return;
    }

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      showMessage("Please fix validation errors", "error");
      return;
    }

    setIsSaving(true);

    try {
      // =========================
      // 1. Upload file first
      // =========================
      const formData = new FormData();

      formData.append("file", selectedFile);
      formData.append("userId", userId);
      formData.append("userCode", userCode);
      formData.append("userName", "Demo User");

      const uploadRes = await fetch("http://localhost:5000/upload-files", {
        method: "POST",
        body: formData,
      });

      const uploadData = await uploadRes.json();

      if (!uploadRes.ok) {
        throw new Error(uploadData.message || "File upload failed");
      }

      // =========================
      // 2. Build payload (MongoDB)
      // =========================
      const payload = {
        user: userId,
        userCode: userCode,

        category: newDoc.category,
        name: newDoc.name,
        type: newDoc.type,

        version: newDoc.version || "v1.0",
        description: newDoc.description,

        fileName: selectedFile.name,
        filePath: uploadData.filePath,
        fileKey: uploadData.fileKey || "",

        mimeType: selectedFile.type,
        fileSize: `${(selectedFile.size / 1024).toFixed(1)} KB`,

        storageType: "local",

        consentEnabled: newDoc.consentEnabled || false,

        accessEntities: newDoc.accessEntities
          ? newDoc.accessEntities.split(",").map((x) => x.trim())
          : [],

        actions: newDoc.actions
          ? newDoc.actions.split(",").map((x) => x.trim())
          : [],
      };

      // =========================
      // 3. Save to MongoDB (Anugya API)
      // =========================
      const config = {
        url: "/api/anugya/documents/create",
        method: "post",
        data: payload,
        headers: {
          Authorization: authState.token,
        },
      };

      const response = await fetchData(config);

      if (response?.status) {
        showMessage("Document saved successfully", "success");

        // =========================
        // 4. Refresh list (optional)
        // =========================
        await fetchMyDocuments();

        // =========================
        // 5. Reset UI
        // =========================
        setShowNewDocForm(false);
        setSelectedFile(null);

        setNewDoc({
          category: "",
          name: "",
          type: "",
          version: "",
          description: "",
          fileName: "",
          filePath: "",
          fileKey: "",
          mimeType: "application/pdf",
          fileSize: "",
          storageType: "local",
          consentEnabled: false,
          accessEntities: "",
          actions: "",
          createdAt: "",
          userId: userId,
          userCode: userCode,
        });
      }
    } catch (error) {
      console.error("FULL ERROR:", error);

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save document";

      setUploadError(errorMessage);
      showMessage(errorMessage, "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-[#f5f7fb] text-slate-800 min-h-screen font-[Inter]">
      <header className="bg-gradient-to-br from-slate-950 via-slate-900 to-blue-900 text-white shadow-xl sticky top-0 z-50 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 lg:px-2 py-3 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div className="flex flex-col">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                </span>
                <span className="text-[9px] lg:text-xs text-green-300 font-semibold">
                  SYSTEM ACTIVE
                </span>
              </div>
              <span className="text-white/30">|</span>
              <h1 className="text-xl lg:text-2xl font-extrabold tracking-tight">
                Anugya
                <span className="ml-6 text-[10px] lg:text-xs font-medium text-slate-100 bg-cyan-900 px-2 py-0.5 rounded-sm align-middle">
                  The Permission / Consent OS
                </span>
              </h1>
            </div>
            <p className="text-[10px] text-blue-200 mt-1 tracking-wide">
              Consent Operating System powered by Think5Whys AI Signals (Identity • Policy • Enforcement • Execution)
            </p>
          </div>

          {/* CENTER: LIVE SIGNAL STRIP */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 bg-white/5 px-3 md:px-4 py-3 rounded-2xl border border-white/10">
            <div
              className="text-center px-2 cursor-pointer hover:bg-white/10 rounded-xl py-2 transition"
              onClick={() => setShowDocs(!showDocs)}
            >
            <div className="text-[10px] md:text-[11px] text-blue-200">
                My Docs
              </div>
              <div className="text-sm md:text-base font-bold text-white">
                {docCount}
              </div>
            </div>
            <div
              className="text-center px-2 cursor-pointer hover:bg-white/10 rounded-xl py-2 transition"
              onClick={() => setShowConsents(!showConsents)}
            >
              <div className="text-[10px] md:text-[11px] text-blue-200">
                Active Inbound Consents
              </div>
              <div className="text-sm md:text-base font-bold text-white">
                {activeConsentCount}
              </div>
            </div>
            <div
              className="text-center px-2 cursor-pointer"
              onClick={() => setShowSignals(true)}
            >
              <div className="text-[10px] md:text-[11px] text-blue-200">
                AI Signals
              </div>

              <div className="flex items-center justify-center gap-1 mt-1">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>

                <span className="text-sm md:text-base font-bold text-green-300">
                  Live
                </span>
              </div>
            </div>
            <div className="text-center px-2">
              <div className="text-[10px] md:text-[11px] text-blue-200">
                Audit Status
              </div>
              <div className="inline-flex items-center justify-center mt-1 px-2 py-0.5 rounded-full bg-yellow-400/10 border border-yellow-400/20">
                <span className="text-xs font-semibold text-yellow-300">
                  Monitoring
                </span>
              </div>
            </div>
            <div className="text-center px-2">
              <div className="text-[10px] md:text-[11px] text-blue-200">
                {userName}
              </div>
              <div className="inline-flex items-center justify-center px-2 py-0.5">
                {isLoggedIn ? (
                  <button
                    onClick={handleLogout}
                    className="w-12 flex items-center justify-center rounded-md bg-red-600 hover:bg-red-700 transition"
                    title="Logout"
                  >
                    ⏻
                  </button>
                ) : (
                  <button
                    onClick={() => navigate("/login")}
                    className="w-12 flex items-center justify-center rounded-md border border-gray-700 text-gray-200 hover:bg-gray-800"
                  >
                    🔐
                  </button>
                )}
              </div>
            </div>
          </div>
        </div> 
      </header>

      <div className="mb-2 bg-slate-300">
        <button
          onClick={() => setShowNewDocForm(!showNewDocForm)}
          className="bg-blue-700 text-white px-4 py-2 rounded-xl text-sm"
        >
          📤 New Document
        </button>

        {showNewDocForm && (
          <div className="mt-2 p-4 bg-gray-400 border rounded-2xl shadow-md space-y-3">
            <div className="grid lg:grid-cols-2 gap-3">
              <input
                placeholder="Document Name"
                value={newDoc.name}
                onChange={(e) => setNewDoc({ ...newDoc, name: e.target.value })}
                className="text-xs lg:text-sm border p-2 rounded-xl"
              />
              <select
                value={newDoc.category}
                onChange={(e) => setNewDoc({ ...newDoc, category: e.target.value })}
                className="text-xs lg:text-sm border p-1 rounded-xl"
              >
                <option value="">Select Category</option>
                {docsState.map((c) => (
                  <option key={c.category} value={c.category}>
                    {c.category}
                  </option>
                ))}
              </select>
              <input
                placeholder="Type (education/employment/etc)"
                value={newDoc.type}
                onChange={(e) => setNewDoc({ ...newDoc, type: e.target.value })}
                className="text-xs lg:text-sm border p-2 rounded-xl"
              />
              <input
                placeholder="File Name"
                value={newDoc.fileName}
                onChange={(e) => setNewDoc({ ...newDoc, fileName: e.target.value })}
                className="text-xs lg:text-sm border p-2 rounded-xl"
              />
              <input
                placeholder="Document Version (e.g. v1.0)"
                value={newDoc.version}
                onChange={(e) =>
                  setNewDoc({ ...newDoc, version: e.target.value })
                }
                className="text-xs lg:text-sm border p-2 rounded-xl"
              />
              <input
                type="date"
                value={newDoc.createdAt}
                onChange={(e) => setNewDoc({ ...newDoc, createdAt: e.target.value })}
                className="text-xs lg:text-sm border p-2 rounded-xl"
              />
            </div>
            <div className="flex flex-col">
              <textarea
                placeholder="Document Description (max 500 characters)"
                value={newDoc.description}
                maxLength={500}
                onChange={(e) =>
                  setNewDoc({ ...newDoc, description: e.target.value })
                }
                className="text-xs lg:text-sm border p-2 rounded-xl md:col-span-2 h-24 resize-none text-white"
              />

              <div className="text-xs text-slate-100">
                {newDoc.description?.length || 0}/500 characters
              </div>
            </div>
            <div className="grid lg:grid-cols-2 gap-3">
              <input
                placeholder="Access Entities (comma separated)"
                value={newDoc.accessEntities}
                onChange={(e) => setNewDoc({ ...newDoc, accessEntities: e.target.value })}
                className="text-xs lg:text-sm border p-2 rounded-xl"
              />
              <input
                placeholder="Actions (comma separated)"
                value={newDoc.actions}
                onChange={(e) => setNewDoc({ ...newDoc, actions: e.target.value })}
                className="text-xs lg:text-sm border p-2 rounded-xl"
              />

              <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    // 20MB LIMIT
                    if (file.size > 20 * 1024 * 1024) {
                      setUploadError("File size must be less than 20MB");
                      return;
                    }
                    setUploadError("");
                    setSelectedFile(file);
                  }}
                  className="text-xs lg:text-sm border p-2 rounded-xl"
                />
                {selectedFile && (
                  <p className="text-xs text-green-600">
                    Selected: {selectedFile.name}
                  </p>
                )}
                {uploadError && (
                  <p className="text-xs text-red-600">{uploadError}</p>
                )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleSaveNewDoc}
                className="bg-green-600 text-white px-4 py-2 w-20 lg:w-24 text-xs lg:text-sm rounded-xl"
              >
                Save
              </button>
              <button
                onClick={() => {
                  setShowNewDocForm(false);
                  setNewDoc({
                    category: "",
                    name: "",
                    version: "",
                    description: "",
                    type: "",
                    fileName: "",
                    filePath: "",
                    fileKey: "",
                    mimeType: "application/pdf",
                    fileSize: "",
                    storageType: "local",
                    consentEnabled: false,
                    accessEntities: "",
                    actions: "",
                    createdAt: "",
                    userId: uId,
                    userCode: uCode,
                  });
                }}
                className="bg-cyan-700 text-white px-4 py-2 w-20 lg:w-24 text-xs lg:text-sm rounded-xl"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      {showDocs && (
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-5 md:p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl lg:text-2xl font-extrabold text-slate-800">
                My Documents
              </h2>
              <button
                onClick={() => setShowDocs(false)}
                className="w-20 lg:w-32 text-xs lg:text-sm p-2 bg-green-700 text-gray-100 hover:text-cyan-50"
              >
                Close
              </button>
            </div>
            {/* CATEGORY GRID (DYNAMIC) */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              {DOCUMENTS.map((section, index) => (
                <div
                  key={index}
                  // onClick={() => setSelectedDoc(doc)}
                  className="border border-cyan-400 bg-gray-200 rounded-2xl p-4 hover:shadow-md transition"
                >
                  <h3 className={`font-bold mb-2 ${section.color}`}>
                    {section.icon} {section.category}
                  </h3>
                  <ul className="space-y-1 text-slate-600">
                    {section.items.map((doc, i) => (
                      <li
                        key={i}
                        onClick={() => setSelectedDoc(doc)}
                        className="hover:text-slate-900 transition cursor-pointer hover:bg-slate-100 px-2 py-1 rounded"
                      >
                        {doc.name || doc}
                      </li>
                    ))}
                  </ul>
                </div>
                
              ))}
            </div>
            {selectedDoc && (
              <div ref={selectedDocRef} className="scroll-mt-24 mt-6 border-t-2 border-blue-700 pt-2">
                <div className="bg-slate-200 rounded-2xl p-5">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-xl font-bold text-slate-800">
                        {selectedDoc.name}
                      </h3>
                      <p className="text-sm text-slate-500 mt-1">
                        Document ID: {selectedDoc.id}
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedDoc(null)}
                      className="w-20 lg:w-24 p-2 text-xs lg:text-sm bg-orange-500 text-white hover:text-yellow-200"
                    >
                      Close
                    </button>
                  </div>
                  <div className="bg-white p-3 rounded-xl">
                    <div className="text-slate-500 text-xs">Version</div>
                    <div className="font-semibold">
                      {selectedDoc.version || "v1.0"}
                    </div>
                  </div>

                  <div className="bg-white p-3 rounded-xl md:col-span-3">
                    <div className="text-slate-500 text-xs">Description</div>
                    <div className="font-semibold text-sm text-slate-700">
                      {selectedDoc.description || "No description provided"}
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-3 mt-4 text-sm">
                    <div className="bg-white p-3 rounded-xl">
                      <div className="text-slate-500 text-xs">Type</div>
                      <div className="font-semibold">
                        {selectedDoc.type || "General"}
                      </div>
                    </div>
                    <div className="bg-white p-3 rounded-xl">
                      <div className="text-slate-500 text-xs">Outbound Consent Enabled</div>
                      <div className="font-semibold">
                        {selectedDoc?.consentEnabled ? "Yes" : "No"}
                      </div>
                    </div>
                    <div className="bg-white p-3 rounded-xl">
                      <div className="text-slate-500 text-xs">Access Entities</div>
                      <div className="font-semibold">
                        {selectedDoc.accessEntities?.length || 0} 
                      </div>
                    </div>
                  </div>

                  {/* ACTION BAR */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-6">
                    <ActionButton
                      label="Open"
                      onClick={() => handleOpen(selectedDoc)}
                    />
                    <ActionButton
                      label="Copy Link"
                      onClick={() => handleCopyLink(selectedDoc)}
                    />
                    <ActionButton
                      label="Share"
                      onClick={() => handleShare(selectedDoc)}
                    />
                    <ActionButton
                      label="Access Log"
                      onClick={() => openPanel("access")}
                    />

                    <ActionButton
                      label="Outbound Consent List"
                      onClick={() => openPanel("consent")}
                    />

                    <ActionButton
                      label="Action List"
                      onClick={() => openPanel("action")}
                    />
                  </div>
                  {showCopyPanel && copiedLink && (
                    <div className="mt-4 p-3 bg-white rounded-xl border text-sm relative">
                      <button
                        onClick={() => setShowCopyPanel(false)}
                        className="absolute top-2 right-2 p-2 text-white bg-blue-700 hover:text-yellow-200 text-xs lg:text-sm w-20 lg:w-32"
                      >
                        ✕ Close
                      </button>
                      <div className="text-slate-600 mb-2">
                        Shareable Link:
                      </div>

                      <div className="text-blue-700 break-all">
                        {copiedLink}
                      </div>

                      {qrDoc && (
                        <div className="mt-3 flex items-center gap-4">
                          <img
                            src={qrDoc}
                            alt="QR Code"
                            className="w-24 h-24 border rounded-lg"
                          />

                          <div className="text-xs text-slate-500">
                            Scan to open document securely via Anugya OS
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {activePanel === "access" && (
                    <div className="bg-white rounded-2xl p-4 border">
                      <h3 className="font-bold text-blue-700 mb-3">
                        Access Log
                      </h3>

                      <div className="space-y-2 text-sm">
                        {ACCESS_LOG.map((log) => (
                          <div
                            key={log.id}
                            className="flex justify-between bg-slate-50 p-3 rounded-xl"
                          >
                            <div className="text-xs text-slate-500 w-6">{log.id}</div>
                            <div className="flex-1 text-slate-800">{log.name}</div>
                            <div className="text-xs text-slate-500">{log.date}</div>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={handleClosePanel}
                        className="text-xs lg:text-sm w-32 lg:w-40 mt-4 p-2 text-slate-100 bg-blue-800 hover:text-white"
                      >
                        Close Panel
                      </button>                      
                    </div>
                  )}

                  {activePanel === "consent" && (
                    <div className="bg-white rounded-2xl p-4 border">
                      <h3 className="font-bold text-green-700 mb-3">
                        Outbound Consent List
                      </h3>

                      <div className="space-y-2 text-sm">
                        {CONSENT_LIST.map((c) => (
                          <div
                            key={c.id}
                            className="bg-slate-50 p-3 rounded-xl"
                          >
                            <div className="flex justify-between">
                              <span className="font-semibold">{c.name}</span>
                              <span className="text-xs text-slate-500">{c.date}</span>
                            </div>

                            <div className="text-xs text-slate-600 mt-1">
                              Type: {c.type}
                            </div>

                            <div className="text-xs text-slate-500">
                              Remarks: {c.remarks}
                            </div>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={handleClosePanel}
                        className="text-xs lg:text-sm w-32 lg:w-40 mt-4 p-2 text-slate-100 bg-blue-800 hover:text-white"
                      >
                        Close Panel
                      </button>
                    </div>
                  )}

                  {activePanel === "action" && (
                    <div className="bg-white rounded-2xl p-4 border">
                      <h3 className="font-bold text-purple-700 mb-3">
                        Action List
                      </h3>

                      <div className="space-y-2 text-sm">
                        {ACTION_LIST.map((a) => (
                          <div
                            key={a.id}
                            className="flex justify-between bg-slate-50 p-3 rounded-xl"
                          >
                            <div className="text-xs text-slate-500 w-6">{a.id}</div>

                            <div className="flex-1 text-slate-800">
                              {a.name}
                            </div>

                            <div className="text-xs font-semibold text-slate-700">
                              {a.action}
                            </div>

                            <div className="text-xs text-slate-500 ml-3">
                              {a.date}
                            </div>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={handleClosePanel}
                        className="text-xs lg:text-sm w-32 lg:w-40 mt-4 p-2 text-slate-100 bg-blue-800 hover:text-white"
                      >
                        Close Panel
                      </button>
                    </div>
                  )}

                  {actionMode === "consent" && (
                    <div className="mt-5 p-4 bg-white rounded-2xl border">

                      <h3 className="font-bold text-green-700 mb-3">
                        Give Consent (Outbound)
                      </h3>

                      {/* USER ID INPUT */}
                      <div className="flex gap-2">
                        <input
                          value={userCode}
                          onChange={(e) => setUserCode(e.target.value)}
                          placeholder="Think5Whys User Id"
                          className="border p-2 rounded-xl w-full"
                        />

                        <button
                          onClick={handleVerify}
                          className="bg-blue-600 text-white px-4 rounded-xl"
                        >
                          Verify
                        </button>
                      </div>

                      {/* ERROR */}
                      {verifyError && (
                        <p className="text-red-500 text-sm mt-2">
                          {verifyError}
                        </p>
                      )}

                      {/* VERIFIED USER */}
                      {verifiedUser && (
                        <div className="mt-3 p-3 bg-green-50 rounded-xl">
                          <p className="font-semibold">
                            {verifiedUser.name}
                          </p>

                          <textarea
                            value={consentNote}
                            onChange={(e) => setConsentNote(e.target.value)}
                            maxLength={2000}
                            placeholder="Consent details (max 2000 chars)"
                            className="w-full mt-2 border p-2 rounded-xl"
                          />

                          <button
                            onClick={handlePostConsent}
                            className="mt-3 bg-green-600 text-white px-4 py-2 rounded-xl"
                          >
                            Post Consent
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ADVANCED ACTIONS */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
                    {selectedDoc.consentEnabled && (
                      <ActionButton
                        label="Give Consent"
                        highlight
                        onClick={() => openActionMode("consent")}
                      />
                    )}
                    <ActionButton
                      label="Audit Trail"
                      onClick={() => openActionMode("audit")}
                    />
                  </div>
                  {actionMode === "audit" && (
                    <div className="mt-5 p-4 bg-white rounded-2xl border">
                      <h3 className="font-bold text-purple-700 mb-3">
                        Audit Trail
                      </h3>
                      <button
                        onClick={() => setActionMode(null)}
                        className="text-xs lg:text-sm w-20 lg:w-32 py-2 text-white bg-blue-700  hover:text-yellow-200"
                      >
                        ✕ Close
                      </button>                      
                      <div className="space-y-2 text-sm">
                        {consentRecords.map((c, index) => (
                          <div
                            key={c.id}
                            className="p-3 bg-slate-50 rounded-xl flex flex-col md:flex-row justify-between gap-2"
                          >
                            <div>
                              <div className="font-semibold">
                                {index + 1}. {c.name}
                              </div>
                              <div className="text-xs text-slate-500">
                                Code: {c.code} | Date: {c.date}
                              </div>
                              <div className="text-xs text-slate-500">
                                Days Active: {c.days}
                              </div>
                            </div>
                            {/* REVOKE ACTION */}
                            <button
                              onClick={() => handleRevoke(c.id)}
                              className="bg-red-600 text-white px-3 py-2 text-xs lg:text-sm w-32 lg:w-40 rounded-lg"
                            >
                              Revoke Access
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {showConsents && (
        <div className="space-y-6">

          {/* ================= HEADER ================= */}
          <section className="max-w-7xl mx-auto px-3 md:px-6 pt-4">
            <div className="bg-white rounded-2xl shadow-sm border p-4 flex justify-between items-center">
              <div>
                <h2 className="text-lg md:text-2xl font-extrabold text-slate-800">
                  Inbound Consent Intelligence Center
                </h2>
                <p className="text-xs text-slate-500">
                  AI-driven consent monitoring • policy enforcement • anomaly detection
                </p>
              </div>

              <button
                onClick={() => setShowConsents(false)}
                className="px-3 py-2 text-xs lg:text-sm w-20 lg:24 bg-slate-900 text-white rounded-xl"
              >
                Close
              </button>
            </div>
          </section>

          {/* ================= CONSENT CARDS ================= */}
          <section className="max-w-7xl mx-auto px-3 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

              {CONSENTS.map((c) => (
                <div
                  key={c.id}
                  className="bg-white border rounded-2xl p-4 shadow-sm hover:shadow-md transition"
                >

                  {/* ENTITY + STATUS */}
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-bold text-blue-700 text-sm md:text-base">
                        {c.entity}
                      </div>
                      <div className="text-xs text-slate-500">
                        {c.purpose}
                      </div>
                    </div>

                    <span className={`text-[10px] px-2 py-1 rounded-full font-semibold
                      ${c.status === "active"
                        ? "bg-green-100 text-green-700"
                        : c.status === "revoked"
                        ? "bg-red-100 text-red-600"
                        : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {c.status}
                    </span>
                  </div>

                  {/* AI SCORE BAR */}
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>AI Risk Score</span>
                      <span>{Math.round(c.aiScore * 100)}%</span>
                    </div>

                    <div className="w-full bg-slate-100 h-2 rounded-full mt-1">
                      <div
                        className={`h-2 rounded-full ${
                          c.aiScore < 0.3
                            ? "bg-green-500"
                            : c.aiScore < 0.6
                            ? "bg-yellow-500"
                            : "bg-red-500"
                        }`}
                        style={{ width: `${c.aiScore * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* META INFO */}
                  <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                    <div className="bg-slate-50 p-2 rounded-lg">
                      <div className="text-slate-500">Policy</div>
                      <div className="font-semibold">{c.policyId}</div>
                    </div>

                    <div className="bg-slate-50 p-2 rounded-lg">
                      <div className="text-slate-500">Access</div>
                      <div className="font-semibold">{c.accessLevel}</div>
                    </div>
                  </div>

                  {/* AI DECISION */}
                  <div className="mt-2 text-xs">
                    <span className="text-slate-500">AI Decision:</span>{" "}
                    <span className={`font-bold ${
                      c.aiDecision === "allow"
                        ? "text-green-600"
                        : c.aiDecision === "monitor"
                        ? "text-yellow-600"
                        : "text-red-600"
                    }`}>
                      {c.aiDecision.toUpperCase()}
                    </span>
                  </div>

                  <div className="text-[11px] text-slate-500 mt-1 italic">
                    {c.aiReason}
                  </div>

                  {/* ACTIONS */}
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    <button className="text-xs bg-slate-100 hover:bg-slate-200 p-2 rounded-lg">
                      View
                    </button>
                    <button className="text-xs bg-slate-100 hover:bg-slate-200 p-2 rounded-lg">
                      Audit
                    </button>
                    <button className="text-xs bg-slate-100 hover:bg-slate-200 p-2 rounded-lg">
                      Trace
                    </button>
                    <button className="text-xs bg-red-50 text-red-600 hover:bg-red-100 p-2 rounded-lg">
                      Revoke
                    </button>
                  </div>

                </div>
              ))}

            </div>
          </section>

          {/* ================= AI SYSTEM OVERVIEW ================= */}
          <section className="max-w-7xl mx-auto px-3 md:px-6">
            <div className="bg-white border rounded-2xl p-4">

              <h3 className="font-bold text-sm md:text-base mb-3">
                AI Consent Intelligence Overview
              </h3>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs md:text-sm">

                <div className="bg-slate-50 p-3 rounded-xl">
                  <div className="text-slate-500">Active Inbound Consents</div>
                  <div className="font-bold text-green-600">
                    {CONSENTS.filter(c => c.status === "active").length}
                  </div>
                </div>

                <div className="bg-slate-50 p-3 rounded-xl">
                  <div className="text-slate-500">Monitored</div>
                  <div className="font-bold text-yellow-600">
                    {CONSENTS.filter(c => c.aiDecision === "monitor").length}
                  </div>
                </div>

                <div className="bg-slate-50 p-3 rounded-xl">
                  <div className="text-slate-500">Blocked/Revoked</div>
                  <div className="font-bold text-red-600">
                    {CONSENTS.filter(c => c.status !== "active").length}
                  </div>
                </div>

                <div className="bg-slate-50 p-3 rounded-xl">
                  <div className="text-slate-500">Avg Risk Score</div>
                  <div className="font-bold text-slate-800">
                    {(
                      CONSENTS.reduce((a, b) => a + b.aiScore, 0) /
                      CONSENTS.length *
                      100
                    ).toFixed(0)}%
                  </div>
                </div>

              </div>
            </div>
          </section>

          {/* ================= OPERATIONS CENTER ================= */}
          <section className="max-w-7xl mx-auto px-3 md:px-6">

            <h3 className="text-sm md:text-lg font-bold mb-2 text-center">
              Consent Operations Center
            </h3>

            <div className="grid lg:grid-cols-3 gap-4">

              {/* Pending */}
              <div className="bg-white border rounded-2xl p-4">
                <h4 className="font-semibold text-sm mb-2">Pending Requests</h4>
                {consentRequests.map((c, i) => (
                  <div key={i} className="text-xs p-2 bg-slate-50 rounded mb-1">
                    {c.from} → {c.type}
                  </div>
                ))}
              </div>

              {/* My Requests */}
              <div className="bg-white border rounded-2xl p-4">
                <h4 className="font-semibold text-sm mb-2">My Requests</h4>
                {myRequests.map((m, i) => (
                  <div key={i} className="text-xs p-2 bg-slate-50 rounded mb-1">
                    {m.to} → {m.status}
                  </div>
                ))}
              </div>

              {/* Completed */}
              <div className="bg-white border rounded-2xl p-4">
                <h4 className="font-semibold text-sm mb-2">Completed Work</h4>
                {completedWork.map((w, i) => (
                  <div key={i} className="text-xs p-2 bg-slate-50 rounded mb-1">
                    {w.action} → {w.place}
                  </div>
                ))}
              </div>

            </div>
          </section>

          {/* ================= EVENT STREAM ================= */}
          <section className="max-w-7xl mx-auto px-3 md:px-6 pb-6">

            <div className="bg-black text-green-400 rounded-2xl p-4">

              <h3 className="text-white font-bold text-sm md:text-base mb-2">
                Live Inbound Consent Event Stream
              </h3>

              <div className="space-y-1 text-xs md:text-sm">
                {eventStream.map((e, i) => (
                  <div key={i} className="opacity-90">
                    {e}
                  </div>
                ))}
              </div>

            </div>
          </section>

        </div>
      )}

      {showSignals && (
        <div className="bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-5xl max-h-[90vh] overflow-auto bg-gray-950 rounded-2xl">           
            <button
              onClick={() => setShowSignals(false)}
              className="text-white bg-red-600 px-2 py-2 rounded text-xs lg:text-sm w-12 lg:w-12"
            >
              ✕
            </button>
            <AnugyaAI />
          </div>
        </div>
      )}

      {/* ================= HERO ================= */}
      <section className="max-w-7xl mx-auto px-4 lg:px-2 py-4 grid lg:grid-cols-2 gap-8">
        <div>
          <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold">
            Human Data Consent Infrastructure Layer
          </span>

          <h2 className="text-xl lg:text-3xl font-extrabold mt-2 leading-tight">
            Identity. Consent. Policy.<br />
            Enforcement. Execution. Audit.
          </h2>

          <p className="mt-2 text-slate-600">
            Anugya governs how human data is accessed, executed, printed, verified and revoked across institutions in real time.
          </p>

          <div className="mt-2 grid grid-cols-2 gap-3 sm:flex sm:flex-wrap">
            <button
              onClick={() => setActiveView("journey")}
              className="bg-blue-600 text-white px-2 py-3 rounded-2xl font-semibold w-full sm:w-auto"
            >
              Start Journey
            </button>

            <button
              onClick={() => setActiveView("demo")}
              className="border px-2 py-3 rounded-2xl text-white bg-blue-700 w-full sm:w-auto"
            >
              Watch Demo
            </button>

            <button
              onClick={() => setActiveView("roi")}
              className="bg-indigo-600 text-white px-2 py-3 rounded-2xl font-semibold w-full sm:w-auto"
            >
              Institutional ROI
            </button>

            <button
              onClick={() => setActiveView("pricing")}
              className="bg-slate-700 text-white px-2 py-3 rounded-2xl font-semibold w-full sm:w-auto"
            >
              Price List
            </button>
          </div>
          {activeView === "journey" && (
             <div className="w-full min-h-screen bg-slate-100 border border-cyan-400">
              <div className="flex justify-end mb-2">
                <button
                  onClick={closeActiveView}
                  className="w-20 p-2 text-xs lg:text-sm bg-blue-700 text-white rounded-xl flex items-center justify-center gap-2"
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>
              <AnugyaStartJourney />
            </div>
          )}

          {activeView === "demo" && (
             <div className="w-full min-h-screen bg-slate-100 border border-green-400">
              <div className="flex justify-end mb-2">
                <button
                  onClick={closeActiveView}
                  className="w-20 p-2 text-xs lg:text-sm bg-blue-700  text-white rounded-xl flex items-center justify-center gap-2"
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>              
              <AnugyaDemoSection />
            </div>
          )}

          {activeView === "roi" && (
            <div className="w-full min-h-screen bg-slate-100 border border-blue-400">
              <div className="flex justify-end mb-2">
                <button
                  onClick={closeActiveView}
                  className="w-20 p-2 text-xs lg:text-sm bg-blue-700 text-white rounded-xl flex items-center justify-center gap-2"
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>              
              <AnugyaROICalc />
            </div>
          )}

          {activeView === "pricing" && (
            <div className="w-full min-h-screen bg-slate-100 border border-purple-400">
              <div className="flex justify-end mb-2">
                <button
                  onClick={closeActiveView}
                  className="w-20 p-2 text-xs lg:text-sm bg-blue-700 text-white rounded-xl flex items-center justify-center gap-2"
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>              
              <AnugyaPriceList />
            </div>
          )}
        </div>

        {/* WHY NOT CLOUD */}
        <div className="bg-white p-6 rounded-3xl shadow-xl">
          <h3 className="font-bold text-xl mb-3">Cloud vs Anugya OS</h3>
          <div className="space-y-2 text-sm">
            <div className="bg-red-50 p-3 rounded-xl">Cloud: stores files</div>
            <div className="bg-green-50 p-3 rounded-xl">Anugya: governs actions on data</div>
            <div className="bg-red-50 p-3 rounded-xl">No lifecycle control</div>
            <div className="bg-green-50 p-3 rounded-xl">Full consent lifecycle engine</div>
            <div className="bg-red-50 p-3 rounded-xl">No print governance</div>
            <div className="bg-green-50 p-3 rounded-xl">Print = controlled action</div>
          </div>
        </div>
      </section>

      {/* ================= IDENTITY / POLICY / ENFORCEMENT ================= */}
      <section className="max-w-7xl mx-auto px-4 lg:px-2 py-4 grid lg:grid-cols-3 gap-4">

        <div className="bg-white p-5 rounded-2xl shadow">
          <h3 className="font-bold">Identity Layer</h3>
          <p className="text-xs text-slate-500 mt-2">
            Aadhaar | ABHA | PAN | Employer ID | Device ID
          </p>
        </div>

        <div className="bg-white p-5 rounded-2xl shadow">
          <h3 className="font-bold">Consent Policy Engine</h3>
          <p className="text-xs text-slate-500 mt-2">
            Duration | Purpose | Action limits | Domain control
          </p>
        </div>

        <div className="bg-white p-5 rounded-2xl shadow">
          <h3 className="font-bold">Enforcement Layer</h3>
          <p className="text-xs text-slate-500 mt-2">
            Revoke | Expiry | Block | Audit | Session control
          </p>
        </div>
      </section>

      {/* ================= ACTION ENGINE ================= */}
      <section className="max-w-7xl mx-auto px-4 lg:px-2 py-4">
        <div className="bg-white px-6 py-2 rounded-3xl shadow-xl">
          <h2 className="text-lg lg:text-xl font-extrabold">Action Execution Engine</h2>

          <div className="grid lg:grid-cols-4 gap-3 mt-1 text-center text-sm">

            <div className="p-4 bg-blue-50 rounded-xl flex flex-col items-center">
              <FontAwesomeIcon icon={faEye} />
              View
            </div>

            <div className="p-4 bg-green-50 rounded-xl flex flex-col items-center">
              <FontAwesomeIcon icon={faDownload} />
              Download
            </div>

            <div className="p-4 bg-purple-50 rounded-xl flex flex-col items-center">
              <FontAwesomeIcon icon={faPrint} />
              Print Consent
            </div>

            <div className="p-4 bg-orange-50 rounded-xl flex flex-col items-center">
              <FontAwesomeIcon icon={faCheck} />
              Verify
            </div>

          </div>
        </div>
      </section>

      {/* ================= PRINT CONSENT ================= */}
      <section className="max-w-7xl mx-auto px-4 lg:px-2 py-4">
        <div className="bg-slate-900 text-white p-6 rounded-3xl">
          <h2 className="text-lg lg:text-xl font-extrabold text-yellow-200">Print Consent System</h2>
          <div className="grid lg:grid-cols-2 gap-4 mt-1 text-sm">
            {[
              "Tokenized Secure Link",
              "Owner Approval Required",
              "Time-bound Execution",
              "Device-bound Session",
              "Mid-print Revocation",
              "Audit Trail Logging",
            ].map((item, i) => (
              <div key={i} className="bg-white/10 p-3 rounded-xl">
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* <footer className="bg-slate-950 text-white text-center p-6 text-sm">
        © 2026 Anugya v4 · Consent Operating System Blueprint · Powered by Think5Whys
      </footer> */}
      <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>

    </div>
  );
}